/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../services/api";

const VerifyOTP = () => {
  const { state } = useLocation();
  const { user, email, password } = state || {};
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/signup");
    }
  }, [user, navigate]);

  // Timer effect for resend button
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer, canResend]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
    
    // Move to next input if value is entered
    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      
      // If current input has value, clear it
      if (otp[index]) {
        setOtp([...otp.map((d, idx) => (idx === index ? "" : d))]);
      } 
      // If current input is empty, move to previous input and clear it
      else if (index > 0) {
        const prevInput = e.target.previousElementSibling;
        if (prevInput) {
          prevInput.focus();
          setOtp([...otp.map((d, idx) => (idx === index - 1 ? "" : d))]);
        }
      }
    }
    // Handle left arrow key
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.target.previousElementSibling?.focus();
    }
    // Handle right arrow key
    else if (e.key === 'ArrowRight' && index < 5) {
      e.target.nextElementSibling?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const formdata = {
        verification_code: otpValue
      }
      const response = await api.verifyOTP(user.user_id, user.access_token, formdata);
      if (response.status === 'success') {
        setVerified(true);
        toast.success("Email verified successfully!");
        Cookies.set('sc_Token', user.access_token);
        localStorage.setItem('user_id', user.user_id);
        setTimeout(() => {
          navigate("/user-details");
        }, 300);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Verification failed. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setCanResend(false);
    setResendTimer(60);
    
    try {
      const formdata = {
        email: email,
        password: password
      }
      const response = await api.signup(formdata);
      if (response.status === 'success') {
        toast.success("Verification code resent to your email");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to resend code");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img 
            src="https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2025-03-18/full_logo.a46683a620fa53c3c554_1742299090688.png" 
            alt="SalesCaptain Logo" 
            className="h-10 object-contain"
          />
        </div>
        
        {verified ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-semibold">
              Email Verified Successfully!
            </h1>
            <p className="text-gray-500">
              Redirecting you to the dashboard...
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-center mb-2">
              Verify your email
            </h1>
            <p className="text-gray-500 text-center mb-8">
              We've sent a verification code to <span className="font-bold text-blue-600">{email}</span>
            </p>
            
            <div className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Enter the 6-digit code sent to your email address to verify your account.</span>
              </div>
            </div>
            
            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <div className="flex justify-center gap-2 mb-2">
                  {otp.map((data, index) => {
                    return (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        value={data}
                        className="w-12 h-12 border rounded-lg text-center text-xl font-medium focus:border-blue-500 focus:outline-none"
                        onChange={e => handleChange(e.target, index)}
                        onKeyDown={e => handleKeyDown(e, index)}
                        onFocus={e => e.target.select()}
                      />
                    );
                  })}
                </div>
              </div>
              
              <button 
                type="submit" 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center"
                disabled={loading || otp.join("").length !== 6}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                Verify Email
              </button>
              
              <div className="text-center mt-4">
                {canResend ? (
                  <button 
                    type="button"
                    onClick={handleResendCode}
                    className="text-blue-600 hover:underline text-sm"
                    disabled={loading}
                  >
                    Didn't receive the code? Resend
                  </button>
                ) : (
                  <div className="text-gray-500 text-sm">
                    Resend code in {resendTimer} seconds
                  </div>
                )}
              </div>
            </form>
            
            <div className="mt-8 flex items-center justify-center text-sm">
              <Link to="/signup" className="flex items-center text-gray-500 hover:text-blue-600">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Sign Up
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyOTP; 